import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useGameStore } from '../../state/game-store.js';
import { inputManager } from '../../input/input-manager.js';
import { soundSystem } from '../../audio/sound-system.js';
import { Crosshair, Camera, OctagonAlert, Menu, RotateCcw } from 'lucide-react';

export const MobileControls: React.FC = () => {
  const gameMode = useGameStore((s) => s.gameMode);
  const isPaused = useGameStore((s) => s.isPaused);
  const setPaused = useGameStore((s) => s.setPaused);
  const cycleCameraMode = useGameStore((s) => s.cycleCameraMode);
  const cameraMode = useGameStore((s) => s.cameraMode);
  const reloadProgress = useGameStore((s) => s.reloadProgress);
  const touchControlsEnabled = useGameStore((s) => s.settings.touchControls);

  // Joystick state
  const joystickBaseRef = useRef<HTMLDivElement>(null);
  const [stickPos, setStickPos] = useState({ x: 0, y: 0 });
  const [isStickActive, setIsStickActive] = useState(false);
  const stickTouchIdRef = useRef<number | null>(null);

  // Aim pad state
  const aimTouchIdRef = useRef<number | null>(null);
  const lastAimPosRef = useRef<{ x: number; y: number } | null>(null);

  // Brake active state
  const [isBraking, setIsBraking] = useState(false);

  // Detect orientation
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(() => {
    const checkOrientation = () => {
      if (typeof window !== 'undefined') {
        setIsPortrait(window.innerHeight > window.innerWidth);
      }
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Joystick Touch Handlers
  const handleJoystickStart = useCallback((e: React.TouchEvent) => {
    if (stickTouchIdRef.current !== null) return;
    const touch = e.changedTouches[0];
    stickTouchIdRef.current = touch.identifier;
    setIsStickActive(true);
    handleJoystickMove(e);
  }, []);

  const handleJoystickMove = useCallback((e: React.TouchEvent) => {
    if (stickTouchIdRef.current === null || !joystickBaseRef.current) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier === stickTouchIdRef.current) {
        const rect = joystickBaseRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const maxRadius = rect.width / 2 - 10;
        let dx = touch.clientX - centerX;
        let dy = touch.clientY - centerY;

        const distance = Math.hypot(dx, dy);
        if (distance > maxRadius) {
          dx = (dx / distance) * maxRadius;
          dy = (dy / distance) * maxRadius;
        }

        setStickPos({ x: dx, y: dy });

        // Normalize inputs
        // Normalized steer: -1 (left) to +1 (right)
        const steer = dx / maxRadius;
        // Normalized throttle: -1 (reverse) to +1 (forward) => note screen dy is inverted (down is positive)
        const throttle = -(dy / maxRadius);

        inputManager.setVirtualMove(throttle, steer);
        break;
      }
    }
  }, []);

  const handleJoystickEnd = useCallback((e: React.TouchEvent) => {
    if (stickTouchIdRef.current === null) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === stickTouchIdRef.current) {
        stickTouchIdRef.current = null;
        setIsStickActive(false);
        setStickPos({ x: 0, y: 0 });
        inputManager.setVirtualMove(0, 0);
        break;
      }
    }
  }, []);

  // Aim Touch Handlers
  const handleAimStart = useCallback((e: React.TouchEvent) => {
    if (aimTouchIdRef.current !== null) return;
    const touch = e.changedTouches[0];
    aimTouchIdRef.current = touch.identifier;
    lastAimPosRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleAimMove = useCallback((e: React.TouchEvent) => {
    if (aimTouchIdRef.current === null || !lastAimPosRef.current) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier === aimTouchIdRef.current) {
        const dx = touch.clientX - lastAimPosRef.current.x;
        const dy = touch.clientY - lastAimPosRef.current.y;

        inputManager.applyVirtualAimDelta(dx, dy);
        lastAimPosRef.current = { x: touch.clientX, y: touch.clientY };
        break;
      }
    }
  }, []);

  const handleAimEnd = useCallback((e: React.TouchEvent) => {
    if (aimTouchIdRef.current === null) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === aimTouchIdRef.current) {
        aimTouchIdRef.current = null;
        lastAimPosRef.current = null;
        break;
      }
    }
  }, []);

  const handleFirePress = () => {
    inputManager.triggerVirtualFire();
    soundSystem.playUiClick();
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(40);
    }
  };

  const handleBrakeDown = () => {
    setIsBraking(true);
    inputManager.setVirtualBrake(true);
    soundSystem.playUiClick();
  };

  const handleBrakeUp = () => {
    setIsBraking(false);
    inputManager.setVirtualBrake(false);
  };

  if (gameMode === 'menu' || !touchControlsEnabled || isPaused) {
    return null;
  }

  const isReloading = reloadProgress < 1.0;

  return (
    <div className="absolute inset-0 z-30 pointer-events-none select-none overflow-hidden touch-none">
      {/* 1. Portrait orientation helper */}
      {isPortrait && (
        <div className="pointer-events-auto absolute top-2 inset-x-4 bg-amber-950/80 border border-amber-600/70 p-2.5 rounded-lg flex items-center justify-between text-xs text-amber-200 z-50 backdrop-blur-md animate-in fade-in">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-amber-400 shrink-0 animate-spin" style={{ animationDuration: '4s' }} />
            <span>Xoay ngang màn hình (Landscape) để có tầm nhìn chiến thuật và điều khiển xe tăng tốt nhất!</span>
          </div>
        </div>
      )}

      {/* 2. Top-right Mobile Action Bar (Pause / Settings) */}
      <div className="pointer-events-auto absolute top-4 right-4 flex items-center gap-2 z-40">
        <button
          onClick={() => {
            soundSystem.playUiClick();
            setPaused(true);
          }}
          className="p-2.5 bg-zinc-900/80 border border-zinc-700/80 hover:border-emerald-500/80 text-zinc-300 hover:text-white rounded-lg backdrop-blur-sm active:scale-95 transition-all shadow-lg"
          title="Tạm dừng / Menu"
        >
          <Menu className="w-5 h-5 text-emerald-400" />
        </button>
      </div>

      {/* 3. Left Virtual Joystick (Differential Movement: Forward / Back / Left / Right) */}
      <div
        className="pointer-events-auto absolute bottom-6 left-6 w-36 h-36 flex items-center justify-center"
        onTouchStart={handleJoystickStart}
        onTouchMove={handleJoystickMove}
        onTouchEnd={handleJoystickEnd}
        onTouchCancel={handleJoystickEnd}
      >
        {/* Joystick Base */}
        <div
          ref={joystickBaseRef}
          className={`relative w-32 h-32 rounded-full border-2 backdrop-blur-md transition-colors flex items-center justify-center ${
            isStickActive
              ? 'border-emerald-500/80 bg-zinc-900/70 shadow-lg shadow-emerald-950/40'
              : 'border-zinc-700/60 bg-zinc-950/50'
          }`}
        >
          {/* Axis markers */}
          <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-[1px] bg-zinc-700/40 pointer-events-none" />
          <div className="absolute inset-y-2 left-1/2 -translate-x-1/2 w-[1px] bg-zinc-700/40 pointer-events-none" />
          <div className="absolute w-16 h-16 rounded-full border border-zinc-700/30 pointer-events-none" />

          {/* Indicator text */}
          <span className="absolute top-1 text-[9px] font-mono text-zinc-500 font-bold uppercase pointer-events-none">
            TIẾN
          </span>
          <span className="absolute bottom-1 text-[9px] font-mono text-zinc-500 font-bold uppercase pointer-events-none">
            LÙI
          </span>
          <span className="absolute left-1 text-[8px] font-mono text-zinc-500 font-bold uppercase pointer-events-none">
            TRÁI
          </span>
          <span className="absolute right-1 text-[8px] font-mono text-zinc-500 font-bold uppercase pointer-events-none">
            PHẢI
          </span>

          {/* Movable Knob */}
          <div
            className={`w-14 h-14 rounded-full border flex items-center justify-center shadow-lg transition-transform ${
              isStickActive
                ? 'bg-gradient-to-br from-emerald-500 to-emerald-700 border-emerald-300 text-zinc-950 shadow-emerald-500/50 scale-105'
                : 'bg-zinc-800/90 border-zinc-600 text-zinc-400'
            }`}
            style={{
              transform: `translate(${stickPos.x}px, ${stickPos.y}px)`,
              transition: isStickActive ? 'none' : 'transform 0.15s ease-out',
            }}
          >
            <div className="w-3 h-3 rounded-full bg-zinc-950/40" />
          </div>
        </div>
      </div>

      {/* 4. Right Aim Area (Touch & Drag anywhere on right half of screen to rotate turret & aim gun) */}
      <div
        className="pointer-events-auto absolute top-20 right-0 bottom-36 w-1/2 flex items-center justify-center"
        onTouchStart={handleAimStart}
        onTouchMove={handleAimMove}
        onTouchEnd={handleAimEnd}
        onTouchCancel={handleAimEnd}
      >
        <div className="px-3 py-1.5 rounded-full bg-zinc-900/40 border border-zinc-800/60 text-[10px] text-zinc-500 font-mono pointer-events-none opacity-40">
          Vuốt để xoay tháp pháo &bull; Swipe to aim
        </div>
      </div>

      {/* 5. Right Action Controls (Fire, Camera, Handbrake) */}
      <div className="pointer-events-auto absolute bottom-6 right-6 flex items-end gap-3 z-40">
        {/* Handbrake Button */}
        <button
          onTouchStart={handleBrakeDown}
          onTouchEnd={handleBrakeUp}
          className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center border backdrop-blur-md active:scale-95 transition-all shadow-md ${
            isBraking
              ? 'bg-amber-600 border-amber-300 text-zinc-950 shadow-amber-900/60'
              : 'bg-zinc-900/80 border-zinc-700/80 text-zinc-300'
          }`}
          title="Phanh gấp"
        >
          <OctagonAlert className="w-5 h-5 text-amber-400" />
          <span className="text-[8px] font-mono font-bold mt-0.5">PHANH</span>
        </button>

        {/* Camera Toggle Button */}
        <button
          onClick={() => {
            cycleCameraMode();
            if (typeof navigator !== 'undefined' && navigator.vibrate) {
              navigator.vibrate(25);
            }
          }}
          className="w-12 h-12 rounded-xl flex flex-col items-center justify-center bg-zinc-900/80 border border-zinc-700/80 text-zinc-300 hover:text-white backdrop-blur-md active:scale-95 transition-all shadow-md"
          title="Đổi góc nhìn Camera"
        >
          <Camera className="w-5 h-5 text-emerald-400" />
          <span className="text-[8px] font-mono font-bold mt-0.5 uppercase">
            {cameraMode === 'third_person' ? '3RD' : cameraMode === 'commander' ? 'CMD' : 'SNIPER'}
          </span>
        </button>

        {/* Fire Button (Primary Action) */}
        <button
          disabled={isReloading}
          onTouchStart={handleFirePress}
          className={`relative w-20 h-20 rounded-full flex flex-col items-center justify-center border-2 backdrop-blur-md active:scale-95 transition-all shadow-xl ${
            isReloading
              ? 'bg-zinc-900/80 border-amber-500/60 text-amber-500 cursor-not-allowed opacity-75'
              : 'bg-gradient-to-br from-emerald-500 to-emerald-700 border-emerald-300 text-zinc-950 shadow-emerald-500/40 hover:scale-105 active:scale-90'
          }`}
        >
          {/* Reload Circular Progress SVG */}
          {isReloading && (
            <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none p-0.5">
              <circle
                cx="38"
                cy="38"
                r="34"
                stroke="rgba(245, 158, 11, 0.2)"
                strokeWidth="4"
                fill="none"
              />
              <circle
                cx="38"
                cy="38"
                r="34"
                stroke="#f59e0b"
                strokeWidth="4"
                fill="none"
                strokeDasharray="213.6"
                strokeDashoffset={213.6 * (1 - reloadProgress)}
                className="transition-all duration-75"
              />
            </svg>
          )}

          <Crosshair className={`w-8 h-8 ${isReloading ? 'text-amber-500 animate-pulse' : 'text-zinc-950'}`} />
          <span className="text-[10px] font-mono font-extrabold tracking-wider uppercase mt-0.5">
            {isReloading ? 'NẠP ĐẠN' : 'KHAI HỎA'}
          </span>
        </button>
      </div>
    </div>
  );
};
